import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { firstValueFrom, Observable, Subscription, throwError, timer } from 'rxjs';
import { catchError, filter, map, switchMap, take, timeout } from 'rxjs/operators';
import { BasketState, BasketStateModel } from '@app/store/state/basket.state';
import { UserState } from '@app/store/state/user.state';
import { Router } from '@angular/router';
import { CurrencyPipe } from '@angular/common';
import {
  ApplyCoupon,
  ApplyGiftCard,
  CheckBalance,
  CleanGiftCard,
  GetBillingSchemes,
  GetTimeSlots,
  RemoveCoupon,
  RemoveGiftCard,
  RemoveProductFromBasket,
  SetBillingInfo,
  SetBuckets,
  SetChange,
  SetTimeWanted,
  SetTipAmount,
  StoreTrancloudCardData,
  SubmitBasket,
  SubmitTrancloudBasket,
  ValidateBasket,
} from '@app/store/actions/basket.action';
import { ToastrService } from 'ngx-toastr';
import { BillingInfo } from 'src/assets/chepri-modules/src/models/olo.billinginfo';
import { SetBanner } from '@app/store/actions/app.action';
import { BasketService } from '@app/providers/basket.service';
import { CapacityService } from '@app/providers/capacity.service';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { BasketChoice, BasketProduct } from 'src/assets/chepri-modules/src/models/olo.basketproduct';
import { RestaurantState } from '@app/store/state/restaurant.state';
import { CmsService } from '@app/providers/cms.service';
import { GiftCardService } from '@app/providers/giftCard.service';
import { SplitPaymentService } from '@app/providers/splitpayment.service';
import { TrancloudProviderService } from '@app/datacap-trancloud/services/trancloud-provider.service';
import { DineInName } from 'src/assets/chepri-modules/src/models/dineinname';
import { SetDineInName, SetPartySize } from '@app/store/actions/user.action';
import {
  GetPaymentSessionStatusResponse,
  PaymentSessionStatus,
} from '@app/models/olo.get-payment-session-status-response';
import { SwipeCardModalComponent } from '@app/components/swipe-card-modal/swipe-card-modal.component';
import { DirectusExpoProvider } from '@app/providers/expo/directus/directus-expo.provider';
import { DirectusApiService } from '@app/providers/expo/directus/directus-api.service';
import { TimeSlot } from '@app/models/capacity.model';
import { GlobalStateModel } from '@app/store/state.model';
import { AlcoholStatus, Product } from '@lib/models/olo.product';
import { Category } from '@lib/models/olo.category';
import { AlcoholStatusModalComponent } from '@app/components/alcohol-status-modal/alcohol-status-modal.component';
import Glob = jest.Glob;

declare var OrderNotifications: any; // Used by Flutter App; DO NOT REMOVE

@Component({
  selector: 'app-checkout',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.scss'],
})
export class KioskCheckoutComponent implements OnInit {
  @Select() basket$: Observable<BasketStateModel>;
  @Select((state: GlobalStateModel) => state.basket.timeSlots) timeSlots$: Observable<TimeSlot[]>;

  firstPaymentMethod: UntypedFormControl = new UntypedFormControl('creditcard');
  secondPaymentMethod: UntypedFormControl = new UntypedFormControl('secondcreditcard');
  maxTableSize = 8;
  selectedTableSize: number = null;
  tableSizeArray = Array;
  buttonAvailable = false;
  showTip = false;
  giftTotal = 0;
  firstGiftApplied = false;
  secondGiftApplied = false;
  needsSecondPayment = false;
  showBalance = false;
  balance = 0;
  giftCardBalance = 0;
  basketSubmit: BillingInfo = new BillingInfo();
  giftCard = false;
  cashPaymentTotal = 0;
  changeDue: number;
  allowSplitPayment = true;
  amExMask = '0000-000000-000009';
  regularMask = '0000-0000-0000-0000';
  currentCardMask = this.regularMask;
  currentCVVMask = '000';
  currentZIPMask = '00000';
  isLoading = false;
  couponCollection: any[] = [];
  fullComp = false;
  showThirdPaymentCard = false;

  tableNumberForm = new UntypedFormGroup({
    tableNumberControl: new UntypedFormControl(),
  });

  couponPromoForm = new UntypedFormGroup({
    couponCode: new UntypedFormControl(),
  });

  userForm = new UntypedFormGroup({
    firstName: new UntypedFormControl(),
    lastName: new UntypedFormControl(),
    emailAddress: new UntypedFormControl(),
  });

  firstGiftCardForm = new UntypedFormGroup({
    cardNumber: new UntypedFormControl(),
    pinNumber: new UntypedFormControl(),
  });

  secondGiftCardForm = new UntypedFormGroup({
    cardNumber: new UntypedFormControl(),
    pinNumber: new UntypedFormControl(),
  });

  firstCashPayment = new UntypedFormGroup({
    amount: new UntypedFormControl(),
  });

  secondCashPayment = new UntypedFormGroup({
    amount: new UntypedFormControl(),
  });

  creditCardPayment = new UntypedFormGroup({
    cardNumber: new UntypedFormControl(),
    expDate: new UntypedFormControl(),
    cvvCode: new UntypedFormControl(),
    zipCode: new UntypedFormControl(),
  });

  timeForm = new UntypedFormGroup({
    time: new UntypedFormControl(null),
  });

  tipForm = new UntypedFormGroup({
    tip: new UntypedFormControl(),
  });

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private store: Store,
    private toastr: ToastrService,
    private currencyPipe: CurrencyPipe,
    private capacityService: CapacityService,
    private modalService: NgbModal,
    private cms: CmsService,
    private gc: GiftCardService,
    private splitPay: SplitPaymentService,
    protected tranCloud: TrancloudProviderService,
    private directusExpo: DirectusExpoProvider,
    private directusApi: DirectusApiService,
    private basketService: BasketService,
  ) {
    this.store.dispatch(new ValidateBasket());
    this.store.dispatch(new SetBanner('BACK', true, true, ''));
    this.basketSubmit = { ...this.store.selectSnapshot((state) => state.basket.billingInfo) };
  }

  /** Payment-session state */
  paymentSessionId: string | null = null; // current session id
  paymentSessionPending = false; // true while we're polling
  paymentSessionApproved = false; // flips to true on "Approved"

  private paymentPollSub?: Subscription; // holds polling subscription

  ngOnInit() {
    this.alcoholModal();
    this.store.dispatch(new GetBillingSchemes());
    const rest = this.store.selectSnapshot((state: GlobalStateModel) => state.restaurant.restaurant);
    this.cms.getCMSLocations().subscribe((res: any) => {
      res.forEach((loc: any) => {
        if (loc.location_name === rest.name) {
          if (loc.max_table_number) {
            this.maxTableSize = Number(loc.max_table_number);
          }
        }
      });
    });
    this.basket$.subscribe((basket) => {
      let giftbalance = 0;
      if (basket && basket.giftCard) {
        basket.giftCard.forEach((card: any) => {
          giftbalance = giftbalance + card.balance;
        });
        this.giftCardBalance = giftbalance;
      }
      if (basket && basket.basket.total === 0) {
        this.fullComp = true;
      }
      if (basket.basket.tip > 0) {
        this.tip.setValue(basket.basket.tip);
      }
      if (basket.basket.tip > 0) {
        this.tip.disable();
      } else {
        this.tip.enable();
      }
    });
    const basket = this.store.selectSnapshot((state: GlobalStateModel) => state.basket);
    if (basket && basket.giftCard) {
      this.giftTotal = this.gc.gcTotal(basket);
      this.needsSecondPayment = basket.giftCard && basket.basket.total > this.giftTotal;
      this.firstPaymentMethod.setValue('giftcard');
      if (basket.giftCard.length === 1) {
        this.firstGiftCardForm.get('cardNumber').setValue(basket.giftCard[0].cardnumber);
        this.firstGiftCardForm.get('pinNumber').setValue(basket.giftCard[0].pin);
      } else if (basket.giftCard.length === 2) {
        this.secondPaymentMethod.setValue('secondgiftcard');
        this.needsSecondPayment = true;
        this.firstGiftCardForm.get('cardNumber').setValue(basket.giftCard[0].cardnumber);
        this.firstGiftCardForm.get('pinNumber').setValue(basket.giftCard[0].pin);
        this.secondGiftCardForm.get('cardNumber').setValue(basket.giftCard[1].cardnumber);
        this.secondGiftCardForm.get('pinNumber').setValue(basket.giftCard[1].pin);
      }
    }
    if (basket && basket.billingInfo && basket.billingInfo.firstname && basket.billingInfo.lastname) {
      this.userForm.get('firstName').setValue(basket.billingInfo.firstname);
      this.userForm.get('lastName').setValue(basket.billingInfo.lastname);
    }
    const user = this.store.selectSnapshot((state: GlobalStateModel) => state.user);
    if (user && user.partySize) {
      this.selectedTableSize = user.partySize - 1;
    }
    this.directusExpo.getKioskCoupons().subscribe((coupons) => {
      this.couponCollection = coupons;
    });
    this.time.valueChanges.subscribe((change) => this.onTimeChange());
    this.timeSlots$
      .pipe(
        filter((ts) => ts !== null),
        take(1),
      )
      .subscribe((ts) => this.time.patchValue(0));
  }

  newPaymentSelected() {
    this.removeGiftCardIndex();
    this.needsSecondPayment = false;
    this.store.dispatch(new CleanGiftCard());
    this.giftTotal = 0;
    this.secondPaymentMethod.setValue('secondcreditcard');
  }

  tableSizeSelected(index: number) {
    this.selectedTableSize = index;
    this.store.dispatch(new SetPartySize(this.selectedTableSize + 1));
  }

  cleanChoices(choices: BasketChoice[]) {
    return choices.filter((choice) => {
      return !(
        choice.name.toLowerCase().includes('included') || choice.name.toLowerCase().includes('additional toppings')
      );
    });
  }

  deleteItem(product: BasketProduct) {
    this.store.dispatch(new RemoveProductFromBasket(product.id.toString())).subscribe(() => {
      this.store.dispatch(new GetTimeSlots(false));
      const selectElement = document.getElementById('time-select') as HTMLSelectElement;
      selectElement.value = 'Select Time';
    });
  }

  editItem(product: BasketProduct) {
    this.router.navigate(['kiosk/product/' + product.productId], {
      queryParams: { edit: true, basketProd: product.id },
      replaceUrl: true,
    });
  }

  // Remove gift card showing from checkout-bag upon switching to another payment method
  removeGiftCard() {
    const basket = this.store.selectSnapshot((state: GlobalStateModel) => state.basket);
    if (basket.giftCard) {
      this.store.dispatch(new CleanGiftCard());
    }
  }

  onTimeChange() {
    if (this.time.value === 'Select Time') {
      this.buttonAvailable = false;
      return;
    }
    const basket = this.store.selectSnapshot((state: GlobalStateModel) => state.basket);
    const selected = basket.timeSlots[this.time.value].value;
    const isManualFire = basket.basket.deliverymode === 'dinein';
    this.store.dispatch(new SetTimeWanted(selected.buckets[selected.buckets.length - 1].time, isManualFire)).subscribe(
      () => {
        this.store.dispatch(new SetBuckets(selected.buckets, isManualFire));
      },
      (error) => {
        // TODO: Fix bug where business hours are used when delivery hours should be used
        this.toastr.warning(error.error.message);
      },
    );
  }

  onCouponChange(couponCode: string) {
    this.applyCoupon(couponCode);
  }

  applyCoupon(couponCode: string) {
    this.store.dispatch(new ApplyCoupon(couponCode)).subscribe(
      () => {
        this.toastr.success('Coupon Applied');
        this.store.dispatch(new ValidateBasket());
      },
      (error) => {
        if (error.error.message) {
          this.toastr.warning(error.error.message);
        } else {
          this.toastr.warning('Coupon Code Is Invalid');
        }
      },
    );
  }

  removeCoupon() {
    this.store.dispatch(new RemoveCoupon()).subscribe(() => {
      this.store.dispatch(new ValidateBasket());
      const couponSelect = document.getElementById('coupon-select') as HTMLSelectElement;
      couponSelect.value = 'Select Comp Coupon';
    });
  }

  scanGiftCard(index: number) {
    // Open the swipe card modal first
    this.swipeCardModal(true);

    // Check if using Olo Pay terminal
    if (this.tranCloud.pinPadDevice.pinPadProvider === 'olo-pay') {
      // For Olo pay terminals, use the basket service to read gift cards
      // Find payment terminal billing scheme
      const state = this.store.selectSnapshot((state) => state);
      const paymentTerminalScheme = state.basket.billingSchemes.find((scheme: any) => scheme.type === 'giftcard');

      if (!paymentTerminalScheme) {
        this.toastr.warning('Payment terminal billing scheme not found');
        this.modalService.dismissAll();
        return;
      }

      // Use the MAC address as the terminal ID for Olo
      const terminalId = this.tranCloud.pinPadDevice.pinPadMACAddress;

      // Create payment session for gift card read (amount 0 since we're only reading)
      this.basketService
        .createPaymentSession(state.basket.basket.id, paymentTerminalScheme.id, terminalId)
        .pipe(
          switchMap((sessionResponse) => {
            // Poll for payment status until complete
            return this.pollGiftCardStatus(state.basket.basket.id, paymentTerminalScheme.id, sessionResponse.id);
          }),
        )
        .subscribe(
          (response) => {
            this.modalService.dismissAll();
            if (response.status === 'Approved') {
              // Extract gift card data from receipt
              const giftCardNumber = response.receiptdata?.Cashier.find((item) => item.Key === 'pan').Value || '';
              const temp = {
                cardNumber: giftCardNumber,
              };
              if (index === 0) {
                this.firstGiftCardForm.patchValue(temp);
              } else {
                this.secondGiftCardForm.patchValue(temp);
              }
            } else {
              this.toastr.warning('Card Denied');
            }
          },
          (error) => {
            this.modalService.dismissAll();
            this.toastr.error('Could not connect to Pin Pad. Please inform a cashier.');
          },
        );
    } else {
      // Original Heartland/Datacap implementation
      this.tranCloud.giftCardSale().subscribe((gc) => {
        this.modalService.dismissAll();
        if (gc && gc.RStream && gc.RStream.CmdStatus === 'Success') {
          const temp = {
            cardNumber: gc.RStream.PrePaidTrack2,
          };
          if (index === 0) {
            this.firstGiftCardForm.patchValue(temp);
          } else {
            this.secondGiftCardForm.patchValue(temp);
          }
        } else {
          this.toastr.warning('Card Denied');
        }
      });
    }
  }

  // Helper method to poll payment session for gift card
  private pollGiftCardStatus(
    basketId: string,
    billingSchemeId: string,
    paymentSessionId: string,
  ): Observable<GetPaymentSessionStatusResponse> {
    return timer(3000, 3000).pipe(
      // First poll after 3 seconds, then every 3 seconds
      switchMap(() => this.basketService.getStatusOfPaymentSession(basketId, billingSchemeId, paymentSessionId)),
      map((response) => {
        // If status is InProgress, continue polling
        if (response.status === PaymentSessionStatus.IN_PROGRESS) {
          return null;
        }
        // Otherwise return the final status
        return response;
      }),
      filter((response) => response !== null),
      take(1),
      timeout(120000), // 2 minute timeout
    );
  }

  checkBalance(index: number, dcTemp: any) {
    this.showBalance = true;

    const temp = dcTemp ? dcTemp : this.gc.gcDetails(index, this.firstGiftCardForm, this.secondGiftCardForm);

    // Ensure we have valid gift card details
    if (!temp || !temp.cardnumber || !temp.pin) {
      this.modalService.dismissAll();
      this.showBalance = false;
      this.toastr.warning('Please enter gift card number and PIN');
      return;
    }

    // Get the current basket state
    const basket = this.store.selectSnapshot((state: GlobalStateModel) => state.basket);

    // Check if this gift card is already applied
    if (basket.giftCard) {
      const isDuplicate = basket.giftCard.some((card: any) => card.cardnumber === temp.cardnumber);
      if (isDuplicate) {
        this.modalService.dismissAll();
        this.showBalance = false;
        this.toastr.warning('This gift card has already been applied');
        return;
      }
    }

    // Use the helper to get the correct gift card billing scheme
    const giftCardScheme = this.getGiftCardBillingScheme(basket);

    if (!giftCardScheme) {
      this.modalService.dismissAll();
      this.showBalance = false;
      this.toastr.warning('Gift card billing scheme not found');
      return;
    }

    // Call basket service directly instead of dispatching action
    this.basketService.checkGiftBalance(giftCardScheme.id, basket.basket.id, temp).subscribe(
      (res: any) => {
        this.modalService.dismissAll();
        this.showBalance = false;

        if (res.balance > 0) {
          this.toastr.info(`Gift-card balance: ${this.currencyPipe.transform(res.balance)}`);
          this.balance = res.balance;
        } else {
          this.toastr.warning('There are no funds on this gift card');
        }
      },
      (err) => {
        this.modalService.dismissAll();
        this.showBalance = false;
        this.toastr.warning(err.error?.message || 'Error checking gift-card balance');
      },
    );
  }

  /** Remove one (or all) applied gift cards and re-enable its form */
  removeGiftCardIndex(index?: 0 | 1): void {
    const basket = this.store.selectSnapshot((s: GlobalStateModel) => s.basket);

    if (index !== undefined) {
      this.store.dispatch(new RemoveGiftCard(index));

      (index === 0 ? this.firstGiftCardForm : this.secondGiftCardForm).enable({ emitEvent: false });
      if (index === 0) {
        this.firstGiftApplied = false;
      } else {
        this.secondGiftApplied = false;
      }
    } else if (basket?.giftCard?.length) {
      for (let i = basket.giftCard.length - 1; i >= 0; i--) {
        this.store.dispatch(new RemoveGiftCard(i));
      }
      this.firstGiftCardForm.enable({ emitEvent: false });
      this.secondGiftCardForm.enable({ emitEvent: false });
      this.firstGiftApplied = this.secondGiftApplied = false;
    }

    const updated = this.store.selectSnapshot((s: GlobalStateModel) => s.basket);
    this.giftTotal = this.gc.gcTotal(updated);
    this.needsSecondPayment = updated.basket.total > this.giftTotal;
  }

  /** Add cash (first or second payment) and work out change-due */
  cashPaymentAdded(index: 0 | 1): void {
    const basket = this.store.selectSnapshot((s: GlobalStateModel) => s.basket);
    const amount = Number(
      index ? this.secondCashPayment.get('amount')!.value : this.firstCashPayment.get('amount')!.value,
    );

    if (!amount || amount <= 0) {
      this.toastr.warning('Enter a cash amount first');
      return;
    }

    this.cashPaymentTotal = amount;

    if (index === 0) {
      this.needsSecondPayment = basket.basket.total - this.giftTotal > amount;

      // If cash is first payment and gift card was selected as second, switch to credit card
      if (this.secondPaymentMethod.value === 'secondgiftcard') {
        this.secondPaymentMethod.setValue('secondcreditcard');
      }
    }

    const due = basket.basket.total - this.giftTotal;
    this.changeDue = amount - due;
    this.store.dispatch(new SetChange(this.changeDue));
  }

  submitOrder() {
    this.isLoading = true;
    const selectElement = document.getElementById('time-select') as HTMLSelectElement;

    // Validate time selection
    if (selectElement.value === 'Select Time') {
      this.isLoading = false;
      this.toastr.warning('Please select a Time');
      return;
    }

    // Validate and set user info
    if (!this.userForm.get('firstName').value || !this.userForm.get('lastName').value) {
      this.isLoading = false;
      this.toastr.warning('Please provide a name');
      return;
    }

    const dineInName: DineInName = {
      firstname: this.userForm.get('firstName').value,
      lastname: this.userForm.get('lastName').value,
    };
    localStorage.setItem('emailAddress', this.userForm.get('emailAddress').value);
    this.store.dispatch(new SetDineInName(dineInName));

    // Get basket state
    const basket = this.store.selectSnapshot((state: GlobalStateModel) => state.basket);
    if (!basket.basketValidation) {
      this.isLoading = false;
      this.toastr.warning('Error validating basket.');
      return;
    }

    const tableNumber = this.tableNumberForm.get('tableNumberControl').value;

    // 1. FULL COMP - Order is fully compensated
    if (basket.basket.subtotal === basket.basket.coupondiscount && basket.basket.total === 0) {
      this.basketSubmit.billingmethod = 'cash';
      this.store.dispatch(new SetBillingInfo(this.basketSubmit)).subscribe(() => {
        this.submitBasket();
      });
      return;
    }

    // 2. SINGLE PAYMENT - Gift Card Only
    if (this.firstPaymentMethod.value === 'giftcard' && !this.needsSecondPayment) {
      const giftCardScheme = this.getGiftCardBillingScheme(basket);
      if (!giftCardScheme) {
        this.isLoading = false;
        this.toastr.warning('Gift card billing scheme not found');
        return;
      }

      this.basketSubmit.billingmethod = 'storedvalue';
      this.basketSubmit.billingschemeid = giftCardScheme.id;
      this.basketSubmit.billingfields = [
        { name: 'number', value: this.firstGiftCardForm.value.cardNumber },
        { name: 'pin', value: this.firstGiftCardForm.value.pinNumber },
      ];
      this.store.dispatch(new SetBillingInfo(this.basketSubmit)).subscribe(() => {
        this.submitBasket();
      });
      return;
    }

    // 3. SINGLE PAYMENT - Credit Card Only
    if (this.firstPaymentMethod.value === 'creditcard' && !this.needsSecondPayment) {
      if (this.tranCloud.pinPadDevice?.pinPadProvider === 'olo-pay') {
        // Olo Pay terminal
        this.processOloPayCardPayment(basket.basket.total).subscribe(
          (cardResult) => {
            if (cardResult.status === 'Approved') {
              this.basketSubmit.billingmethod = 'paymentsession';
              this.basketSubmit.paymentsessionid = cardResult.paymentSessionId;
              this.basketSubmit.billingschemeid = cardResult.billingSchemeId;
              this.store.dispatch(new SetBillingInfo(this.basketSubmit)).subscribe(() => {
                this.submitBasket();
              });
            } else {
              this.isLoading = false;
              this.toastr.warning('Credit card payment was not approved');
            }
          },
          (error) => {
            this.isLoading = false;
            this.toastr.error('Error processing credit card payment');
          },
        );
      } else {
        // Traditional credit card swipe
        this.store.dispatch(new ValidateBasket('false', true)).subscribe(
          () => this.submitTrancloudBasket(),
          (error) => {
            this.isLoading = false;
            this.toastr.warning(error.error?.message || error);
          },
        );
      }
      return;
    }

    // 4. SINGLE PAYMENT - Cash Only
    if (this.firstPaymentMethod.value === 'paywithcash' && !this.needsSecondPayment) {
      if (this.cashPaymentTotal === 0) {
        this.isLoading = false;
        this.toastr.warning('Please enter a cash amount');
        return;
      }

      this.basketSubmit.billingmethod = 'cash';
      this.store.dispatch(new SetBillingInfo(this.basketSubmit)).subscribe(() => {
        this.submitBasket();
      });
      return;
    }

    // 5. SPLIT PAYMENTS
    // All split payment scenarios are handled by the split payment service

    // 5A. Gift Card + Other Payment
    if (this.firstPaymentMethod.value === 'giftcard' && this.needsSecondPayment) {
      const remaining = basket.basket.total - this.giftTotal;

      if (this.secondPaymentMethod.value === 'secondcreditcard') {
        if (this.tranCloud.pinPadDevice?.pinPadProvider === 'olo-pay') {
          // Olo Pay for credit card portion
          this.processOloPayCardPayment(remaining).subscribe(
            (cardResult) => {
              if (cardResult.status === 'Approved') {
                this.splitPay
                  .multiplePayment(this.giftTotal, null, 0, 0, true, tableNumber, false, false, {
                    paymentSessionId: cardResult.paymentSessionId,
                    billingSchemeId: cardResult.billingSchemeId,
                  })
                  .subscribe(
                    () => this.splitPaySuccess(),
                    (error) => this.splitPayFail(error),
                  );
              } else {
                this.isLoading = false;
                this.toastr.warning('Credit card payment was not approved');
              }
            },
            (error) => {
              this.isLoading = false;
              this.toastr.error('Error processing credit card payment');
            },
          );
        } else {
          // Traditional credit card entry
          if (!this.validateCreditCardForm()) {
            this.isLoading = false;
            this.toastr.warning('Please enter credit card information');
            return;
          }

          this.splitPay.multiplePayment(this.giftTotal, this.creditCardPayment, 0, 0, true, tableNumber).subscribe(
            () => this.splitPaySuccess(),
            (error) => this.splitPayFail(error),
          );
        }
        return;
      }

      if (this.secondPaymentMethod.value === 'secondgiftcard') {
        // Two gift cards
        this.splitPay.multiplePayment(this.giftTotal, null, 0, 0, true, tableNumber).subscribe(
          () => this.splitPaySuccess(),
          (error) => this.splitPayFail(error),
        );
        return;
      }

      if (this.secondPaymentMethod.value === 'secondpaywithcash') {
        // Gift card + Cash
        this.splitPay.multiplePayment(this.giftTotal, null, Number(remaining), 0, true, tableNumber).subscribe(
          () => this.splitPaySuccess(),
          (error) => this.splitPayFail(error),
        );
        return;
      }
    }

    // 5B. Cash + Other Payment
    if (this.firstPaymentMethod.value === 'paywithcash' && this.needsSecondPayment) {
      const remaining = basket.basket.total - this.cashPaymentTotal;

      if (this.secondPaymentMethod.value === 'secondcreditcard') {
        if (this.tranCloud.pinPadDevice?.pinPadProvider === 'olo-pay') {
          // Olo Pay for credit card portion
          this.processOloPayCardPayment(remaining).subscribe(
            (cardResult) => {
              if (cardResult.status === 'Approved') {
                this.splitPay
                  .multiplePayment(0, null, Number(this.cashPaymentTotal), 0, true, tableNumber, false, false, {
                    paymentSessionId: cardResult.paymentSessionId,
                    billingSchemeId: cardResult.billingSchemeId,
                  })
                  .subscribe(
                    () => this.splitPaySuccess(),
                    (error) => this.splitPayFail(error),
                  );
              } else {
                this.isLoading = false;
                this.toastr.warning('Credit card payment was not approved');
              }
            },
            (error) => {
              this.isLoading = false;
              this.toastr.error('Error processing credit card payment');
            },
          );
        } else {
          // Traditional credit card entry
          if (!this.validateCreditCardForm()) {
            this.isLoading = false;
            this.toastr.warning('Please enter credit card information');
            return;
          }

          this.splitPay
            .multiplePayment(0, this.creditCardPayment, Number(this.cashPaymentTotal), 0, true, tableNumber)
            .subscribe(
              () => this.splitPaySuccess(),
              (error) => this.splitPayFail(error),
            );
        }
        return;
      }

      if (this.secondPaymentMethod.value === 'secondgiftcard') {
        // Cash + Gift card - check if there's remaining balance for third payment
        const giftCardApplied = this.giftTotal;
        const afterGift = basket.basket.total - this.cashPaymentTotal - giftCardApplied;

        if (afterGift > 0) {
          // There's a remaining balance - need third payment (card)
          if (this.tranCloud.pinPadDevice?.pinPadProvider === 'olo-pay') {
            // Olo Pay: Start payment session for remaining amount
            this.processOloPayCardPayment(afterGift).subscribe(
              (cardResult) => {
                if (cardResult.status === 'Approved') {
                  // Submit all payments: cash, gift card, and card session
                  this.splitPay
                    .multiplePayment(
                      this.giftTotal,
                      null,
                      Number(this.cashPaymentTotal),
                      0,
                      true,
                      tableNumber,
                      false,
                      false,
                      {
                        paymentSessionId: cardResult.paymentSessionId,
                        billingSchemeId: cardResult.billingSchemeId,
                      },
                    )
                    .subscribe(
                      () => this.splitPaySuccess(),
                      (error) => this.splitPayFail(error),
                    );
                } else {
                  this.isLoading = false;
                  this.toastr.warning('Credit card payment was not approved');
                }
              },
              (error) => {
                this.isLoading = false;
                this.toastr.error('Error processing credit card payment');
              },
            );
          } else {
            // Manual card entry - validate form first
            if (!this.validateCreditCardForm()) {
              this.isLoading = false;
              this.toastr.warning('Please enter credit card information for the remaining balance');
              return;
            }
            this.splitPay
              .multiplePayment(
                this.giftTotal,
                this.creditCardPayment,
                Number(this.cashPaymentTotal),
                0,
                true,
                tableNumber,
              )
              .subscribe(
                () => this.splitPaySuccess(),
                (error) => this.splitPayFail(error),
              );
          }
        } else {
          // No remaining balance - cash + gift card covers everything
          this.splitPay.multiplePayment(remaining, null, Number(this.cashPaymentTotal), 0, true, tableNumber).subscribe(
            () => this.splitPaySuccess(),
            (error) => this.splitPayFail(error),
          );
        }
        return;
      }

      if (this.secondPaymentMethod.value === 'secondpaywithcash') {
        // All cash (single payment)
        this.basketSubmit.billingmethod = 'cash';
        this.store.dispatch(new SetBillingInfo(this.basketSubmit)).subscribe(() => {
          this.submitBasket();
        });
        return;
      }
    }

    // Fallback for unknown payment combination
    this.isLoading = false;
    this.toastr.warning('Invalid payment combination');
  }

  private validateCreditCardForm(): boolean {
    return !!(
      this.creditCardPayment.value.cardNumber &&
      this.creditCardPayment.value.expDate &&
      this.creditCardPayment.value.cvvCode &&
      this.creditCardPayment.value.zipCode
    );
  }

  // Helper for success/failure for split
  splitPaySuccess() {
    this.isLoading = false;
    this.toastr.success('The Order has been completed!');
    this.router.navigateByUrl('/kiosk/confirmation');
  }
  splitPayFail(error: any) {
    this.isLoading = false;
    this.modalService.dismissAll();
    if (error.error && error.error.message) {
      this.toastr.warning(error.error.message);
    } else {
      this.toastr.warning(typeof error === 'string' ? error : error.message);
    }
  }
  finalizeOrder() {
    this.store.dispatch(new SetBillingInfo(this.basketSubmit)).subscribe(() => {
      this.submitBasket();
    });
  }

  singleGiftCardPayment() {
    this.store.dispatch(new ValidateBasket());
    const basket = this.store.selectSnapshot((state: GlobalStateModel) => state.basket);

    const giftCardScheme = this.getGiftCardBillingScheme(basket);

    this.basketSubmit.billingmethod = 'storedvalue';
    this.basketSubmit.billingschemeid = giftCardScheme?.id;
    this.basketSubmit.billingfields = [
      {
        name: 'number',
        value: this.firstGiftCardForm.value.cardNumber,
      },
      {
        name: 'pin',
        value: this.firstGiftCardForm.value.pinNumber,
      },
    ];
    this.basketSubmit.usertype = basket.billingInfo?.usertype;
  }

  submitBasket() {
    this.store.dispatch(new SetBillingInfo(this.basketSubmit)).subscribe(() => {
      this.store
        .dispatch(new SubmitBasket({ tableNumber: this.tableNumberForm.get('tableNumberControl').value }))
        .subscribe(
          () => {
            this.isLoading = false;
            this.modalService.dismissAll();
            this.toastr.success('The order has been completed!');
            this.router.navigateByUrl('/kiosk/confirmation');
          },
          (error) => {
            this.isLoading = false;
            this.basketSubmit = { ...this.store.selectSnapshot((state) => state.basket.billingInfo) };
            if (this.basketSubmit.billingmethod === 'paymentsession') {
              this.basketService
                .cancelAPaymentSession(
                  this.store.selectSnapshot((state: GlobalStateModel) => state.basket.basket.id),
                  this.basketSubmit.billingschemeid,
                  this.basketSubmit.paymentsessionid,
                )
                .subscribe(() => this.toastr.info('Payment canceled'));
            }
            if (error.error && error.error.message) {
              this.toastr.warning(error.error.message);
            } else {
              this.toastr.warning(typeof error === 'string' ? error : error.message);
            }
          },
        );
    });
  }

  submitTrancloudBasket() {
    let modalRef;
    const modalOptions: NgbModalOptions = { centered: true, size: 'lg', windowClass: 'hideScroll' };
    modalRef = this.modalService.open(SwipeCardModalComponent, modalOptions);
    modalRef.componentInstance.isGiftCard = false;
    modalRef.componentInstance.xClicked.subscribe(() => {
      this.modalService.dismissAll();
      this.isLoading = false;
    });
    this.store.dispatch(new SetBillingInfo(this.basketSubmit)).subscribe(() => {
      this.store
        .dispatch(new SubmitTrancloudBasket({ tableNumber: this.tableNumberForm.get('tableNumberControl').value }))
        .subscribe(
          () => {
            this.isLoading = false;
            this.modalService.dismissAll();
            this.toastr.success('The order has been completed!');
            this.router.navigateByUrl('/kiosk/confirmation');
          },
          (error) => {
            this.isLoading = false;
            this.modalService.dismissAll();
            this.basketSubmit = { ...this.store.selectSnapshot((state) => state.basket.billingInfo) };
            if (error.error && error.error.message) {
              this.toastr.warning(error.error.message);
            } else {
              this.toastr.warning(typeof error === 'string' ? error : error.message);
            }
          },
        );
    });
  }

  checkAndSwitchMask(input: any) {
    if (input.target.value.length > 17 && this.currentCardMask === this.amExMask) {
      this.currentCardMask = this.regularMask;
      this.currentCVVMask = '000';
    } else if (this.currentCardMask === this.regularMask && input.target.value.length < 19) {
      this.currentCardMask = this.amExMask;
      this.currentCVVMask = '0000';
    }
  }

  swipeCardModal(isGiftCard: boolean) {
    let modalRef;
    const modalOptions: NgbModalOptions = { centered: true, size: 'lg', windowClass: 'hideScroll' };
    modalRef = this.modalService.open(SwipeCardModalComponent, modalOptions);
    modalRef.componentInstance.isGiftCard = isGiftCard;
    modalRef.componentInstance.xClicked.subscribe(() => {
      this.modalService.dismissAll();
    });
  }

  alcoholModal() {
    const menu = this.store.selectSnapshot((state: GlobalStateModel) => state.restaurant.menu);
    const order = this.store.selectSnapshot((state: GlobalStateModel) => state.basket.basket);
    const orderItems: Product[] = [];
    menu.categories.forEach((category: Category) => {
      category.products.forEach((product: Product) => {
        if (order.products.find((item: BasketProduct) => String(item.productId) === String(product.id))) {
          orderItems.push(product);
        }
      });
    });
    if (orderItems.some((item) => item.alcoholstatus !== AlcoholStatus.NONE)) {
      let modalRef;
      const modalOptions: NgbModalOptions = { centered: true, size: 'lg', windowClass: 'hideScroll' };
      modalRef = this.modalService.open(AlcoholStatusModalComponent, modalOptions);
      modalRef.componentInstance.xClicked.subscribe(() => {
        this.modalService.dismissAll();
      });
    }
  }

  async applyTip(amount: number) {
    await firstValueFrom(this.store.dispatch(new SetTipAmount(amount.toFixed(2))));
  }

  removeTip(): void {
    this.tip.setValue(null);
    this.store.dispatch(new SetTipAmount((0).toFixed(2)));
  }

  get couponCode() {
    return this.couponPromoForm.get('couponCode').value;
  }

  get time() {
    return this.timeForm.get('time');
  }

  get tip() {
    return this.tipForm.get('tip');
  }

  /** Apply a looked-up gift card and lock its form */
  applyGiftCard(index: 0 | 1): void {
    const basket = this.store.selectSnapshot((s: GlobalStateModel) => s.basket);
    const form = index === 0 ? this.firstGiftCardForm : this.secondGiftCardForm;

    // Check if this exact form is already disabled (which means it's already applied)
    if (form.disabled) {
      this.toastr.warning('This gift card has already been applied');
      return;
    }

    // Get the gift card details from the form
    const cardNumber = form.get('cardNumber')?.value;
    const pinNumber = form.get('pinNumber')?.value;

    if (!cardNumber || !pinNumber) {
      this.toastr.warning('Please enter gift card number and PIN');
      return;
    }

    const giftCardScheme = this.getGiftCardBillingScheme(basket);
    if (!giftCardScheme) {
      this.toastr.warning('Gift-card billing scheme not found');
      return;
    }

    // Check if this gift card is already applied
    if (basket.giftCard) {
      const isDuplicate = basket.giftCard.some((card: any) => card.cardnumber === cardNumber);
      if (isDuplicate) {
        this.toastr.warning('This gift card has already been applied');
        return;
      }
    }

    // Dispatch CheckBalance to add the card to state
    const checkBalancePayload = {
      cardnumber: cardNumber,
      pin: pinNumber,
    };

    this.store.dispatch(new CheckBalance(checkBalancePayload)).subscribe(
      () => {
        // After CheckBalance succeeds, check if it was added to state
        const updatedBasket = this.store.selectSnapshot((s: GlobalStateModel) => s.basket);
        const addedCard = updatedBasket.giftCard?.find((card: any) => card.cardnumber === cardNumber);

        if (!addedCard || addedCard.balance <= 0) {
          this.toastr.warning('Gift card has no balance or was not added');
          return;
        }

        // Now dispatch ApplyGiftCard
        this.store.dispatch(new ApplyGiftCard(true)).subscribe(() => {
          this.giftTotal = this.gc.gcTotal(updatedBasket);

          // disable the form that was just applied
          form.disable({ emitEvent: false });
          if (index === 0) {
            this.firstGiftApplied = true;
          } else {
            this.secondGiftApplied = true;
          }

          const remaining = updatedBasket.basket.total - this.giftTotal;
          this.needsSecondPayment = remaining > 0;

          // Check if we need to show third payment card option
          if (this.firstPaymentMethod.value === 'paywithcash' && this.cashPaymentTotal > 0) {
            const afterCashAndGift = updatedBasket.basket.total - this.cashPaymentTotal - this.giftTotal;
            this.showThirdPaymentCard = afterCashAndGift > 0;
          }

          this.toastr.success(
            remaining > 0
              ? `Applied – remaining ${this.currencyPipe.transform(remaining)}`
              : 'Gift card covers the full amount!',
          );
        });
      },
      (err) => {
        this.toastr.warning(err.error?.message || 'Error applying gift card');
      },
    );
  }

  /** Returns amount still owed after gift-cards & cash have been applied. */
  outstanding(): number {
    const basket = this.store.selectSnapshot((state: GlobalStateModel) => state.basket.basket);
    return Math.max(
      basket.total - // ticket total incl. tip & tax
        this.gc.gcTotal(basket) - // gift-card dollars already applied
        this.cashPaymentTotal, // cash already applied
      0,
    );
  }

  startPaymentSession(amount: number): void {
    if (this.paymentSessionPending) {
      this.toastr.info('Payment session already running');
      return;
    }

    const state = this.store.selectSnapshot((s: GlobalStateModel) => s.basket);
    const basketId = state.basket.id;

    /* 1️⃣ Resolve credit-card billing-scheme and terminal id */
    const creditScheme = state.billingSchemes.find((s: any) => s.type === 'paymentterminal');
    if (!creditScheme) {
      this.toastr.error('Credit-card billing scheme not found');
      return;
    }
    const billingSchemeId = creditScheme.id;
    const terminalId = this.tranCloud.pinPadDevice.pinPadMACAddress;

    /* 2️⃣ Create the session */
    this.paymentSessionPending = true;
    this.basketService.createPaymentSession(basketId, billingSchemeId, terminalId, amount).subscribe({
      next: (res) => {
        this.paymentSessionId = res.id;

        /* 3️⃣ Begin polling every 2 s until terminal responds */
        this.paymentPollSub = timer(0, 2000)
          .pipe(
            switchMap(() =>
              this.basketService.getStatusOfPaymentSession(basketId, billingSchemeId, this.paymentSessionId!),
            ),
          )
          .subscribe((statusRes) => {
            if (statusRes.status === PaymentSessionStatus.APPROVED) {
              this.paymentSessionPending = false;
              this.paymentSessionApproved = true;
              this.paymentPollSub?.unsubscribe();
              this.toastr.success('Card approved – ready to submit order');
            }
            if (statusRes.status === PaymentSessionStatus.DECLINED || statusRes.status === PaymentSessionStatus.ERROR) {
              this.paymentSessionPending = false;
              this.paymentSessionId = null;
              this.paymentPollSub?.unsubscribe();
              this.toastr.warning('Card declined or terminal error');
            }
          });
      },
      error: () => {
        this.paymentSessionPending = false;
        this.toastr.error('Failed to start payment session');
      },
    });
  }

  cancelPaymentSession(sessionId?: string): void {
    const id = sessionId ?? this.paymentSessionId;
    if (!id) {
      this.toastr.info('No payment session to cancel');
      return;
    }

    /* stop local polling immediately */
    this.paymentPollSub?.unsubscribe();
    this.paymentSessionPending = false;
    this.paymentSessionApproved = false;

    /* call DELETE /paymentsessions/{id} */
    const basket = this.store.selectSnapshot((s: GlobalStateModel) => s.basket);
    const scheme = basket.billingSchemes.find((s: any) => s.type === 'paymentterminal')?.id;

    this.basketService
      .cancelAPaymentSession(basket.basket.id, scheme, id) // :contentReference[oaicite:2]{index=2}
      .subscribe({
        next: () => {
          this.paymentSessionId = null;
          this.toastr.success('Payment session cancelled');
        },
        error: () => this.toastr.error('Unable to cancel payment session'),
      });
  }

  private getGiftCardBillingScheme(basket: any) {
    // Returns the billing scheme object for stored value/giftcard payments
    return basket.billingSchemes?.find(
      (sche: any) => sche.type?.toLowerCase() === 'giftcard' || sche.type?.toLowerCase() === 'storedvalue',
    );
  }

  /**
   * Helper method to handle payment with Olo Pay for credit/debit cards
   * Creates a payment session and polls for completion
   */
  private processOloPayCardPayment(amount: number): Observable<any> {
    // Open swipe card modal
    this.swipeCardModal(false);

    const basket = this.store.selectSnapshot((state: GlobalStateModel) => state.basket);

    // Find payment terminal billing scheme
    const paymentTerminalScheme = basket.billingSchemes.find((scheme: any) => scheme.type === 'paymentterminal');

    if (!paymentTerminalScheme) {
      this.modalService.dismissAll();
      return throwError(() => new Error('Payment terminal billing scheme not found'));
    }

    // For OloPay, the MAC address is the terminal ID
    const terminalId = this.tranCloud.pinPadDevice.pinPadMACAddress;

    // Create payment session
    return this.basketService.createPaymentSession(basket.basket.id, paymentTerminalScheme.id, terminalId, amount).pipe(
      switchMap((sessionResponse) => {
        // Poll for payment status until complete
        return this.pollPaymentSessionStatus(basket.basket.id, paymentTerminalScheme.id, sessionResponse.id).pipe(
          map((statusResponse) => {
            this.modalService.dismissAll();

            /* 🆕 pull receipt fields when Approved */
            if (statusResponse.status === PaymentSessionStatus.APPROVED) {
              const cust = statusResponse.receiptdata?.Customer ?? [];
              const get = (k: string) => cust.find((i) => i.Key === k)?.Value || '';

              const cardNumber = get('pan') || 'XXXXXXXXXXXX';
              const cardType = get('paymentMethod') || 'Credit';
              const authCode = get('authCode') || 'AUTH';
              const refNo = get('txRef') || sessionResponse.id;

              /* 🆕 stash for printer / later Expo push */
              this.store.dispatch(new StoreTrancloudCardData(cardNumber, cardType, authCode, refNo));

              /* return card fields so callers (split-pay) have them too */
              return {
                status: statusResponse.status,
                paymentSessionId: sessionResponse.id,
                billingSchemeId: paymentTerminalScheme.id,
                cardNumber,
                cardType,
                authCode,
                refNo,
              };
            }

            /* declined or error – keep old return shape */
            return {
              status: statusResponse.status,
              paymentSessionId: sessionResponse.id,
              billingSchemeId: paymentTerminalScheme.id,
            };
          }),
        );
      }),
      catchError((error) => {
        this.modalService.dismissAll();
        return throwError(() => error);
      }),
    );
  }

  /**
   * Polls the payment session status until it's complete (approved, declined, or error)
   */
  private pollPaymentSessionStatus(
    basketId: string,
    billingSchemeId: string,
    paymentSessionId: string,
  ): Observable<GetPaymentSessionStatusResponse> {
    return timer(3000, 3000).pipe(
      // First poll after 3 seconds, then every 3 seconds
      switchMap(() => this.basketService.getStatusOfPaymentSession(basketId, billingSchemeId, paymentSessionId)),
      map((response) => {
        // If status is InProgress, continue polling
        if (response.status === PaymentSessionStatus.IN_PROGRESS) {
          return null;
        }
        // Otherwise return the final status
        return response;
      }),
      filter((response) => response !== null),
      take(1),
      timeout(120000), // 2 minute timeout
    );
  }

  /** True when the specified index (0/1) already has an applied card */
  isGiftCardApplied(index: 0 | 1): boolean {
    const basket = this.store.selectSnapshot((s: GlobalStateModel) => s.basket);
    return !!basket.giftCard && basket.giftCard.length > index;
  }

  /** Show the "second gift card" option when appropriate */
  canAddSecondGiftCard(): boolean {
    const basket = this.store.selectSnapshot((s: GlobalStateModel) => s.basket);

    // Allow second gift card if:
    // 1. We need a second payment
    // 2. We have less than 2 gift cards applied (max is 2)
    const currentGiftCardCount = basket.giftCard ? basket.giftCard.length : 0;
    return this.needsSecondPayment && currentGiftCardCount < 2;
  }
}
